import { createElement, useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Home, Utensils, ShoppingBag, Briefcase, Users, MessageSquareQuote, HelpCircle,
  Newspaper, Check, Boxes, MapPin, Scissors, GraduationCap, Dumbbell,
  PawPrint, Wrench, HardHat, Compass, Calendar, Scale, type LucideIcon,
} from 'lucide-react';
import { INDUSTRIES } from '@palacete/shared/presets';
import { api } from '../../lib/api';
import type { OnboardingCatalog, PresetSummary } from '../../lib/types';
import { pickLoc } from '../../lib/collections';
import Modal from '../ui/Modal';

interface Props {
  existingKeys: string[];
  onClose: () => void;
  onAdded: (createdKeys: string[]) => void;
}

const ICONS: Record<string, LucideIcon> = {
  home: Home, utensils: Utensils, 'shopping-bag': ShoppingBag, briefcase: Briefcase,
  users: Users, 'message-square-quote': MessageSquareQuote, 'help-circle': HelpCircle,
  newspaper: Newspaper, 'map-pin': MapPin, scissors: Scissors,
  'graduation-cap': GraduationCap, dumbbell: Dumbbell, 'paw-print': PawPrint,
  wrench: Wrench, 'hard-hat': HardHat, compass: Compass, calendar: Calendar, scale: Scale,
};
const iconFor = (name: string): LucideIcon => ICONS[name] ?? Boxes;

function resolveLabel(
  key: string,
  preset: PresetSummary,
  industryKey: string | null,
  form: 'singular' | 'plural',
): string {
  if (industryKey) {
    const ind = INDUSTRIES.find((i) => i.key === industryKey);
    const override = ind?.labelOverrides?.[key];
    if (override) return override[form].es ?? '';
  }
  return pickLoc(preset.labels[form], 'es') ?? key;
}

export default function AddContentModal({ existingKeys, onClose, onAdded }: Props) {
  const [catalog, setCatalog] = useState<OnboardingCatalog | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const submittingRef = useRef(false);
  const [error, setError] = useState('');
  const [industryKey, setIndustryKey] = useState<string | null>(null);

  useEffect(() => {
    api.onboarding.catalog().then((c) => {
      setCatalog(c);
      setIndustryKey(c.industry);
    }).catch((e) => setError((e as Error).message));
  }, []);

  // Divide los disponibles según la industria seleccionada:
  // - recommended: presets del wizard no instalados aún
  // - others: compatibles pero no en recommended
  // Si no hay industria (genérico) o compatible está vacío, se muestran todos sin filtro.
  const { recommended, others } = useMemo(() => {
    if (!catalog) return { recommended: [] as string[], others: [] as string[] };
    const have = new Set(existingKeys);
    const allAvailable = Object.keys(catalog.presets).filter((k) => !have.has(k));

    // Usa INDUSTRIES importado localmente — no depende del API para datos estáticos.
    const ind = industryKey ? INDUSTRIES.find((i) => i.key === industryKey) : null;
    const recSet = new Set(ind?.recommended ?? []);
    const compatSet = ind && ind.compatible.length > 0 ? new Set(ind.compatible) : null;

    const visible = compatSet ? allAvailable.filter((k) => compatSet.has(k)) : allAvailable;

    return {
      recommended: visible.filter((k) => recSet.has(k)),
      others: visible.filter((k) => !recSet.has(k)),
    };
  }, [catalog, existingKeys, industryKey]);

  const hasAny = recommended.length > 0 || others.length > 0;

  function toggle(key: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  async function add() {
    if (selected.size === 0 || submittingRef.current) return;
    submittingRef.current = true;
    setSaving(true);
    setError('');
    try {
      const { created } = await api.onboarding.instantiate([...selected], industryKey ?? undefined);
      onAdded(created);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      submittingRef.current = false;
      setSaving(false);
    }
  }

  const currentIndustryLabel = INDUSTRIES.find((i) => i.key === industryKey);

  return (
    <Modal title="¿Qué sección de contenido quieres añadir?" onClose={onClose} wide>
      <div className="space-y-4">
        {!catalog && !error && <p className="text-sm text-ui-text-muted">Cargando opciones…</p>}
        {error && <p className="text-sm text-ui-danger">{error}</p>}

        {catalog && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-ui-text-muted shrink-0">Ver etiquetas para:</span>
            <select
              value={industryKey ?? ''}
              onChange={(e) => setIndustryKey(e.target.value || null)}
              className="text-xs bg-ui-surface border border-ui-border rounded px-2 py-1.5 text-ui-text focus:outline-none focus:border-ui-accent"
            >
              <option value="">Genérico</option>
              {INDUSTRIES.map((ind) => (
                <option key={ind.key} value={ind.key}>{ind.label.es}</option>
              ))}
            </select>
          </div>
        )}

        {catalog && !hasAny && (
          <p className="text-sm text-ui-text-subtle">Ya tienes activado todo el contenido disponible.</p>
        )}

        {/* Sección recomendada para la industria seleccionada */}
        {catalog && recommended.length > 0 && (
          <div className="space-y-2">
            {currentIndustryLabel && (
              <p className="text-xs font-semibold uppercase tracking-wider text-ui-text-subtle">
                Recomendado para {pickLoc(currentIndustryLabel.label, 'es')}
              </p>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {recommended.map((key, i) => {
                const preset = catalog.presets[key];
                if (!preset) return null;
                return (
                  <Card
                    key={key}
                    preset={preset}
                    displayLabel={resolveLabel(key, preset, industryKey, 'plural')}
                    selected={selected.has(key)}
                    onToggle={() => toggle(key)}
                    delay={0.03 * i}
                  />
                );
              })}
            </div>
          </div>
        )}

        {/* Resto de opciones disponibles */}
        {catalog && others.length > 0 && (
          <div className="space-y-2">
            {recommended.length > 0 && (
              <p className="text-xs font-semibold uppercase tracking-wider text-ui-text-subtle">
                Más opciones
              </p>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {others.map((key, i) => {
                const preset = catalog.presets[key];
                if (!preset) return null;
                return (
                  <Card
                    key={key}
                    preset={preset}
                    displayLabel={resolveLabel(key, preset, industryKey, 'plural')}
                    selected={selected.has(key)}
                    onToggle={() => toggle(key)}
                    delay={0.03 * (recommended.length + i)}
                  />
                );
              })}
            </div>
          </div>
        )}

        {catalog && hasAny && (
          <div className="flex justify-end gap-3 pt-2 border-t border-ui-border">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-ui-text-muted hover:text-ui-text">
              Cancelar
            </button>
            <motion.button
              type="button"
              onClick={add}
              disabled={selected.size === 0 || saving}
              whileTap={{ scale: 0.97 }}
              className="px-4 py-2 bg-ui-accent text-ui-text-inverse text-sm font-medium rounded-md disabled:opacity-40 hover:opacity-90 transition-opacity"
            >
              {saving ? 'Añadiendo…' : `Añadir${selected.size ? ` (${selected.size})` : ''}`}
            </motion.button>
          </div>
        )}
      </div>
    </Modal>
  );
}

function Card({ preset, displayLabel, selected, onToggle, delay }: {
  preset: PresetSummary;
  displayLabel: string;
  selected: boolean;
  onToggle: () => void;
  delay: number;
}) {
  return (
    <motion.button
      type="button"
      onClick={onToggle}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      whileHover={{ y: -2 }}
      whileTap={{ scale: 0.98 }}
      className={`relative text-left p-4 rounded-xl border transition-colors ${
        selected ? 'bg-ui-accent/10 border-ui-accent/50' : 'bg-ui-bg border-ui-border hover:border-ui-border-strong'
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        {createElement(iconFor(preset.icon), { className: `w-5 h-5 ${selected ? 'text-ui-accent' : 'text-ui-text-muted'}` })}
        <motion.span
          animate={{ scale: selected ? 1 : 0, opacity: selected ? 1 : 0 }}
          className="w-5 h-5 rounded-full bg-ui-accent flex items-center justify-center"
        >
          <Check className="w-3.5 h-3.5 text-ui-bg" />
        </motion.span>
      </div>
      <p className="text-sm font-medium text-ui-text mt-2">{displayLabel}</p>
      <div className="flex flex-wrap gap-1 mt-2">
        {preset.fields.slice(0, 4).map((f, i) => (
          <span key={i} className="text-xs text-ui-text-subtle bg-ui-text/[0.05] rounded px-1.5 py-0.5">
            {pickLoc(f.label, 'es')}
          </span>
        ))}
      </div>
    </motion.button>
  );
}
